import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

/** A quotation is a SalesOrder with documentType 'quotation' (db-sales.js). */
export interface QuotationLine {
  productId?: string | null;
  description?: string;
  quantity: number;
  unitPrice: number;
  discount?: number;
  discountType?: 'fixed' | 'percent';
  cgstRate?: number;
  sgstRate?: number;
  igstRate?: number;
  lineTotal?: number;
  /**
   * Sent by the API and read by the quotation mapping below, but absent from
   * this interface — which is why the frontend typecheck was red and why
   * "Per Pack Qty Missing" (checklist item 53) looked like a data bug when the
   * data was arriving all along. Field set mirrors db-sales.js:61-67.
   */
  hsnSac?: string;
  unit?: string;
  packSize?: string;
  qtyPerPack?: number;
  totalPackSize?: string;
  volumeLiters?: number;
}

export interface Quotation {
  _id: string;
  orderNumber?: string;
  documentNumber?: string;
  date: string;
  documentType: string;
  status: string;
  customerId?: { _id: string; name: string; email?: string; phone?: string } | string | null;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  crmDealId?: string | null;
  crmLeadId?: string | null;
  /** Absent when fetched with `compact` — list/dashboard views never render
   *  line items, and a full order's lines are most of its 14MB-at-scale
   *  payload. Use the single-record GET for full detail. */
  lines?: QuotationLine[];
  subTotal?: number;
  taxTotal?: number;
  grandTotal?: number;
  notes?: string;
  dueDate?: string | null;
  shippingAddress?: Record<string, unknown> | null;
  billingAddress?: Record<string, unknown> | null;
  shippingCharge?: number;
  freightCharges?: number;
  createdBy?: { display_name?: string; username?: string } | string | null;
  createdByName?: string;
  originatingRepId?: string | null;
  lostReasonId?: { _id: string; name: string } | string | null;
  lostReasonName?: string;
  lostReasonNote?: string;
  lostAt?: string | null;
  lostBy?: any;
}

const KEY = ['quotations'];

export function useQuotations(status?: string, limit = 200, compact = false) {
  return useQuery({
    queryKey: [...KEY, 'list', status || 'all', limit, compact],
    queryFn: async () => {
      const p = new URLSearchParams({ documentType: 'quotation', limit: String(limit) });
      if (status && status !== 'all') p.set('status', status);
      if (compact) p.set('compact', 'true');
      const { data } = await api.get<{ data: Quotation[] }>(`/api/sales-ops/orders?${p}`);
      return data.data ?? [];
    },
  });
}

export type QuotationSort = 'date_desc' | 'date_asc' | 'amount_desc' | 'amount_asc' | 'customer_asc' | 'customer_desc';

export interface QuotationListParams {
  /** A single status, 'all', or a comma-separated list (e.g. from the
   *  dashboard's Open Quotations tile) — the backend accepts both. */
  status?: string;
  search?: string;
  fromDate?: string;
  toDate?: string;
  sort?: QuotationSort;
  /** Employee-wise filter — only honored server-side for superadmin/department_admin. */
  createdBy?: string;
  /** A catalog product's _id — matches lines.productId server-side (already
   *  supported by GET /api/sales-ops/orders, just not exposed on this page
   *  until now). */
  productId?: string;
  page?: number;
  limit?: number;
}

export interface QuotationListResult {
  data: Quotation[];
  pagination: { total: number; page: number; limit: number; totalPages: number };
  stats?: { totalValue: number; openCount: number };
}

/**
 * Paginated + server-filtered list, for the Quotations table. The plain
 * useQuotations() above fetches everything up to `limit` in one shot — fine
 * for a dashboard KPI sum, but with 4000+ real historical quotations that
 * meant every page load shipped and rendered the entire history at once.
 * Search/status/date filtering happen on the server (GET /orders already
 * supports all of this — see src/api-sales/orders.js), so the client only
 * ever holds one page's worth of rows.
 */
export function useQuotationsPaged(params: QuotationListParams) {
  const { status, search, fromDate, toDate, sort, createdBy, productId, page = 1, limit = 50 } = params;
  return useQuery({
    queryKey: [...KEY, 'paged', { status, search, fromDate, toDate, sort, createdBy, productId, page, limit }],
    queryFn: async () => {
      const p = new URLSearchParams({
        documentType: 'quotation',
        compact: 'true',
        page: String(page),
        limit: String(limit),
      });
      if (status && status !== 'all') p.set('status', status);
      if (search?.trim()) p.set('search', search.trim());
      if (fromDate) p.set('fromDate', fromDate);
      if (toDate) p.set('toDate', toDate);
      if (sort) p.set('sort', sort);
      if (createdBy) p.set('createdBy', createdBy);
      if (productId) p.set('productId', productId);
      const { data } = await api.get<QuotationListResult>(`/api/sales-ops/orders?${p}`);
      return data;
    },
    placeholderData: (prev) => prev,
  });
}

export interface CreateQuotationPayload {
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  notes?: string;
  crmDealId?: string;
  crmLeadId?: string;
  lines: QuotationLine[];
}

export function useQuotationMutations() {
  const qc = useQueryClient();
  const invalidate = () => Promise.all([
    qc.invalidateQueries({ queryKey: KEY }),
    qc.invalidateQueries({ queryKey: ['invoice-requests'] }),
    qc.invalidateQueries({ queryKey: ['pipeline'] }),
    qc.invalidateQueries({ queryKey: ['deals'] }),
    qc.invalidateQueries({ queryKey: ['leads'] }),
  ]);

  const create = useMutation({
    mutationFn: async (payload: CreateQuotationPayload) => {
      const { data } = await api.post('/api/sales-ops/orders', {
        ...payload,
        documentType: 'quotation',
        date: new Date().toISOString(),
      });
      return data as Quotation;
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: CreateQuotationPayload }) => {
      const { data } = await api.put(`/api/sales-ops/orders/${id}`, payload);
      return data as Quotation;
    },
    onSuccess: invalidate,
  });

  /**
   * Convert an accepted quotation into an Invoice Request (spec §3.2).
   * Line items are sent with their captured rate; the request snapshots them,
   * so a later catalog price change cannot alter what was quoted.
   */
  const convertToInvoiceRequest = useMutation({
    mutationFn: async ({ quotation, productNames }: {
      quotation: Quotation;
      productNames: Record<string, { name: string; hsnSac?: string }>;
    }) => {
      const items = (quotation.lines ?? []).map((l) => {
        const meta = productNames[String(l.productId)] || { name: l.description || 'Service' };
        const taxRate = (l.cgstRate || 0) + (l.sgstRate || 0) + (l.igstRate || 0);
        return {
          catalogItemId: l.productId || null,
          name: meta.name || l.description || 'Service',
          description: l.description || '',
          notes: (l as { notes?: string }).notes || '',
          hsnSac: meta.hsnSac || l.hsnSac || '',
          unit: l.unit || '',
          packSize: l.packSize || '',
          qtyPerPack: Number(l.qtyPerPack) || 0,
          totalPackSize: l.totalPackSize || '',
          volumeLiters: Number(l.volumeLiters) || 0,
          quantity: l.quantity,
          rate: l.unitPrice,
          discountPercent: l.discountType === 'percent' ? (l.discount || 0) : 0,
          taxRate,
        };
      });

      const { data } = await api.post('/api/invoice-requests', {
        clientId: typeof quotation.customerId === 'object' ? quotation.customerId?._id : quotation.customerId,
        clientName: quotation.customerName || '',
        sourceQuotationId: quotation._id,
        sourceQuotationNumber: quotation.documentNumber || quotation.orderNumber || '',
        sourceDealId: quotation.crmDealId || null,
        items,
        notes: quotation.notes || '',
        dueDate: quotation.dueDate || null,
        shippingAddress: quotation.shippingAddress || null,
        billingAddress: quotation.billingAddress || null,
        shippingCharge: quotation.shippingCharge ?? quotation.freightCharges ?? 0,
      });
      // Once sent for invoice request, we confirm it to lock it from edits.
      await api.post(`/api/sales-ops/orders/${quotation._id}/confirm`);
      
      return data as { id: string; number: string };
    },
    onSuccess: invalidate,
  });

  const confirm = useMutation({
    mutationFn: async (id: string) => (await api.post(`/api/sales-ops/orders/${id}/confirm`)).data as Quotation,
    onSuccess: invalidate,
  });

  const markLost = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: { lostReasonId: string; lostReasonNote?: string; closeDeal?: boolean } }) =>
      (await api.post(`/api/sales-ops/orders/${id}/mark-lost`, payload)).data,
    onSuccess: invalidate,
  });

  return { create, update, confirm, markLost, convertToInvoiceRequest };
}
